import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import TopBar from '../components/TopBar'
import TabNav from '../components/TabNav'
import MobileSidebar from '../components/MobileSidebar'
import TabContent from '../components/TabContent'
import SearchModal from '../components/SearchModal'
import { SearchProvider } from '../contexts/SearchContext'
import { LinkProvider } from '../contexts/LinkContext'
import { useCampaign } from '../contexts/CampaignContext'
import { useAuth } from '../contexts/AuthContext'
// Maps an entity type to the tab that displays it. Shared with SearchModal so
// wiki-links and search results resolve types the same way.
import { ENTITY_TYPE_TO_TAB } from '../utils/entityNavigation'
import './CampaignPage.css'

function CampaignPage() {
  const { campaign, canEdit, isModuleEnabled } = useCampaign()
  const { authEpoch } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [activeSubTab, setActiveSubTab] = useState(null) // For My Character subtabs
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [focusEntity, setFocusEntity] = useState(null) // { tab, id } from a wiki-link click
  const [counts, setCounts] = useState({
    quests: 0,
    locations: 0,
    npcs: 0,
    sessions: 0,
    items: 0
  })

  // Campaign-aware browser tab title; restore the site title on unmount so
  // navigating away (e.g. to /settings) doesn't leave a stale campaign name.
  useEffect(() => {
    document.title = campaign ? `${campaign.name} — Start your Quest` : 'Start your Quest'
    return () => { document.title = 'Start your Quest' }
  }, [campaign])

  // The URL is the single source of truth for which tab is showing.
  //
  // This effect used to be the only half that existed: it read `?tab=` on
  // arrival, but nothing ever wrote it back, so clicking a tab changed state
  // without touching history and Back skipped straight out of the campaign.
  // handleTabChange now navigates instead of setting state directly, which
  // means a tab click, a pasted link and a Back press all land here by the
  // same path and cannot disagree.
  //
  // isModuleEnabled stays in the deps on purpose: on the first render the
  // campaign hasn't loaded yet, so it reports every module disabled and a
  // deep link would resolve to overview. Re-running once the campaign
  // arrives corrects that.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const requested = params.get('tab') || 'overview'
    const tab = isModuleEnabled(requested) ? requested : 'overview'
    setActiveTab(tab)
    // Sub-tabs only exist on My Character. MyCharacterTab keeps its own
    // selection and ignores a null hint, so clearing this doesn't reset the
    // user's place — it just stops overriding it from the URL.
    setActiveSubTab(tab === 'character' ? params.get('sub') : null)
  }, [location.search, isModuleEnabled])

  // Fetch counts on mount and when the campaign (or its enabled modules) changes.
  useEffect(() => {
    if (!campaign) return
    const wanted = ['quests', 'locations', 'npcs', 'sessions', 'items'].filter((k) => isModuleEnabled(k))
    if (wanted.length === 0) return

    const fetchCounts = async () => {
      try {
        const results = await Promise.all(
          wanted.map((k) => fetch(`/api/${k}`).then((r) => (r.ok ? r.json() : [])))
        )
        const next = { quests: 0, locations: 0, npcs: 0, sessions: 0, items: 0 }
        wanted.forEach((key, i) => {
          const rows = Array.isArray(results[i]) ? results[i] : []
          next[key] = key === 'quests'
            ? rows.filter((q) => q.is_visible === 1 && q.completed !== 1).length
            : rows.filter((r) => r.is_visible === 1).length
        })
        setCounts(next)
      } catch (err) {
        console.error('Error fetching counts:', err)
      }
    }

    fetchCounts()
    // authEpoch: see the identical comment in ItemsTab.jsx.
  }, [campaign, isModuleEnabled, authEpoch])

  // Overview is the campaign's landing tab, so it gets the bare URL rather
  // than ?tab=overview — a shared link to the campaign shouldn't carry a
  // parameter that only restates the default.
  const tabSearch = (tab, subtab) => {
    const params = new URLSearchParams()
    if (tab !== 'overview') params.set('tab', tab)
    if (tab === 'character' && subtab) params.set('sub', subtab)
    const query = params.toString()
    return query ? `?${query}` : ''
  }

  const handleTabChange = (tab, subtab = null) => {
    setIsSidebarOpen(false) // Close sidebar on mobile after selection

    const search = tabSearch(tab, subtab)
    // Re-clicking the tab you're already on must not stack a duplicate
    // history entry — otherwise Back appears to do nothing several times.
    if (search === location.search) return

    // Push, don't replace: these entries are the whole point of the change.
    // The effect above turns the new URL back into activeTab/activeSubTab.
    navigate({ search })
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  // Called when a [[wiki-link]]'s "Go to card" or a search result is clicked:
  // switch to the entity's tab and ask that tab to open its detail view.
  const openEntity = (type, id) => {
    const tab = ENTITY_TYPE_TO_TAB[type]
    if (!tab) return
    // Set the focus before navigating so the tab has it on its first render.
    // handleTabChange no-ops when the entity already lives on the current
    // tab, and that's fine — focusEntity alone is what opens the detail.
    setFocusEntity({ tab, id })
    handleTabChange(tab)
  }

  return (
    <SearchProvider>
      <LinkProvider onOpenEntity={openEntity}>
        <div className="app-container">
          <TopBar onMenuToggle={toggleSidebar} />
          <MobileSidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            counts={counts}
            campaign={campaign}
          />
          <TabNav activeTab={activeTab} onTabChange={handleTabChange} counts={counts} />
          <TabContent
            activeTab={activeTab}
            activeSubTab={activeSubTab}
            focusEntity={focusEntity}
            onConsumeFocus={() => setFocusEntity(null)}
          />
          <SearchModal onNavigate={handleTabChange} onOpenEntity={openEntity} canEdit={canEdit} />
        </div>
      </LinkProvider>
    </SearchProvider>
  )
}

export default CampaignPage
